import { useMemo } from "react";
import { pick } from "lodash";
import { useBrandMutation } from "utils/apollo/hooks/brand";
import { makeClearNestedListFieldCache } from "utils/apollo/cacheHelpers";
import useCreateValidSubmitHandler, {
  UseCreateValidSubmitHandlerOptions,
} from "components/Shared/Forms/useCreateValidSubmitHandler";
import { assertPresence, getAssertedString } from "utils/assertions";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import {
  skuBrandOrderingPlatformFields,
  SkuBrandOrderingPlatformFormFields,
  processApolloError,
} from "./Form";

const fragment = gql(/* GraphQL */ `
  fragment UseEditSkuBrandOrderingPlatform on SkuBrandOrderingPlatform {
    id
    itemCode
    wholesaleUnitPrice
    skuId
    brandOrderingPlatform {
      id
      orderingPlatformId
    }
    skuBrandDistributionCentersToEdit: skuBrandDistributionCentersList(
      orderBy: CREATED_AT_ASC
    ) {
      id
      brandDistributionCenter {
        id
        distributionCenterId
        distributionCenter {
          id
          ...DistributionCenterSelectOption
        }
      }
    }
  }
`);

export const updateM2mFragment = gql(/* GraphQL */ `
  fragment UpdateSkuBrandOrderingPlatformM2M on Mutation {
    setSkuDistributionCenters(
      input: {
        skuId: $skuId
        orderingPlatformId: $orderingPlatformId
        distributionCenterIds: $distributionCenterIds
      }
    ) {
      sku {
        id
      }
      query {
        skuBrandOrderingPlatform(id: $id) {
          id
          ...UseEditSkuBrandOrderingPlatform
          ...EditSkuBrandOrderingPlatformDetails
          ...CustomerSkusTableRow
          ...SkuPanelSkuBrandOrderingPlatform
        }
      }
    }
  }
`);

const updateMutation = gql(/* GraphQL */ `
  mutation UpdateSkuBrandOrderingPlatform(
    $id: UUID!
    $data: SkuBrandOrderingPlatformPatch!
    $skuId: UUID!
    $orderingPlatformId: UUID!
    $distributionCenterIds: [UUID!]!
  ) {
    updateSkuBrandOrderingPlatform(input: { id: $id, patch: $data }) {
      skuBrandOrderingPlatform {
        id
      }
    }
    ...UpdateSkuBrandOrderingPlatformM2M
  }
`);

type UpdateResult = DocumentType<typeof updateMutation>;

export default function useEditSkuBrandOrderingPlatform(
  skuBrandOrderingPlatform: FragmentType<typeof fragment> | null | undefined,
  options?: UseCreateValidSubmitHandlerOptions<UpdateResult>,
) {
  const sbop = getFragmentData(fragment, skuBrandOrderingPlatform);

  const { skuId } = sbop || {};
  const { orderingPlatformId } = sbop?.brandOrderingPlatform || {};

  const defaultValues = useMemo(() => {
    if (!sbop) return;
    return {
      ...pick(sbop, skuBrandOrderingPlatformFields),
      distributionCenterIds:
        sbop.skuBrandDistributionCentersToEdit?.map((sbdc) =>
          getAssertedString(sbdc.brandDistributionCenter?.distributionCenterId),
        ) || [],
    };
  }, [sbop]);

  const [performUpdate, { loading: isUpdating }] = useBrandMutation(
    updateMutation,
    {
      update: (cache) => {
        const clearNestedListFieldCache = makeClearNestedListFieldCache(cache);
        clearNestedListFieldCache("Sku", ["skuBrandDistributionCenters"]);
        clearNestedListFieldCache("BrandDistributionCenter", [
          "skuBrandDistributionCenters",
        ]);
      },
    },
  );

  const [handleValidSubmit] = useCreateValidSubmitHandler(
    async (formData: SkuBrandOrderingPlatformFormFields) => {
      assertPresence(sbop?.id, "Expected ID to be available!");

      assertPresence(orderingPlatformId);
      assertPresence(skuId);

      const { distributionCenterIds, skuId: formSkuId, ...sbopData } = formData;

      const { data } = await performUpdate({
        variables: {
          id: sbop.id,
          data: sbopData,
          skuId,
          orderingPlatformId,
          distributionCenterIds,
        },
      });

      assertPresence(data);

      return data;
    },
    {
      processApolloError,
      successMessage: "Product details updated successfully.",
      ...options,
    },
  );

  return {
    brandOrderingPlatformId: sbop?.brandOrderingPlatform?.id,
    defaultValues,
    handleValidSubmit,
    isUpdating,
    orderingPlatformId,
    skuId,
  };
}
